"use client";

import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface FAQSectionProps {
  variant?: "simple" | "tabbed";
}

const categories = [
  "Product & Compatibility",
  "Buying Wheels",
  "Shipping & Delivery",
  "Returns & Refunds",
  "Security & Privacy",
];

const faqsByCategory: Record<string, { question: string; answer: string }[]> = {
  "Product & Compatibility": [
    {
      question: "How do I find the right wheels for my car?",
      answer: "You can use our license plate search feature to find compatible wheels for your vehicle. Simply enter your license plate number and we'll show you wheels that match your car's specifications.",
    },
    {
      question: "Can I sell my old wheels through your platform?",
      answer: "Yes! WheelTox allows you to list and sell your used wheels. Simply create an account, upload photos of your wheels, and set your price.",
    },
    {
      question: "How do I know if the wheels I'm buying are high-quality?",
      answer: "All wheels on WheelTox are inspected and quality-checked before listing. We provide detailed condition reports and photos for each wheel.",
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept all major credit cards, debit cards, and secure online payment methods including MobilePay.",
    },
    {
      question: "Do you offer international shipping?",
      answer: "Currently, we offer shipping within Denmark. We're working on expanding our shipping options to other countries.",
    },
    {
      question: "How long does delivery take?",
      answer: "Most orders arrive within 2-5 business days, depending on shipment size and destination.",
    },
    {
      question: "Do I need to install the wheels myself?",
      answer: "We recommend having wheels installed by a professional. We can provide recommendations for trusted installation partners in your area.",
    },
  ],
  "Buying Wheels": [
    {
      question: "How do I place an order?",
      answer: "Browse our selection, add items to your cart, and proceed to checkout. You'll need to create an account or log in to complete your purchase.",
    },
    {
      question: "Can I return wheels if they don't fit?",
      answer: "Yes, we offer returns within 14 days of delivery if the wheels don't fit your vehicle. Please ensure they are in original condition.",
    },
    {
      question: "Are there any warranties on the wheels?",
      answer: "All refurbished wheels come with a 6-month warranty covering manufacturing defects.",
    },
  ],
  "Shipping & Delivery": [
    {
      question: "How are wheels packaged for shipping?",
      answer: "Each wheel is securely wrapped with protective materials to prevent scratches and damage during transit.",
    },
    {
      question: "Can I track my order?",
      answer: "Yes, you'll receive tracking information via email once your order is dispatched.",
    },
    {
      question: "What if my order arrives damaged?",
      answer: "Contact us immediately with photos of the damage. We'll arrange a replacement or refund.",
    },
  ],
  "Returns & Refunds": [
    {
      question: "What is your return policy?",
      answer: "We accept returns within 14 days of delivery for items in original condition. Refunds are processed within 5-7 business days.",
    },
    {
      question: "How do I initiate a return?",
      answer: "Log into your account, go to your orders, and select the item you wish to return. Follow the instructions to generate a return label.",
    },
  ],
  "Security & Privacy": [
    {
      question: "Is my payment information secure?",
      answer: "Yes, we use industry-standard encryption to protect your payment information. We never store your full card details.",
    },
    {
      question: "How do you use my personal data?",
      answer: "We only use your data to process orders and improve your experience. Read our Privacy Policy for full details.",
    },
  ],
};

const simpleFaqs = [
  {
    question: "How do I find the right wheels for my car?",
    answer: "You can use our wheel finder tool by entering your vehicle's make, model, and year. Our system will show you all compatible wheels for your car.",
  },
  {
    question: "Can I sell my old wheels through your platform?",
    answer: "Yes! We offer a wheel selling service. Simply submit your wheel details and photos, and we'll provide you with a quote.",
  },
  {
    question: "How do I know if the wheels I'm buying are high-quality?",
    answer: "All our wheels go through a rigorous quality inspection process. We only sell wheels that meet our strict quality standards.",
  },
  {
    question: "What payment methods do you accept?",
    answer: "We accept all major credit cards, PayPal, and bank transfers. Payment is secure and encrypted.",
  },
  {
    question: "Do you offer international shipping?",
    answer: "Yes, we ship to most countries worldwide. Shipping costs and delivery times vary by location.",
  },
  {
    question: "How long does delivery take?",
    answer: "Delivery typically takes 3-7 business days for domestic orders and 7-14 business days for international orders.",
  },
  {
    question: "Do I need to install the wheels myself?",
    answer: "We recommend having your wheels installed by a professional. We can also connect you with certified installers in your area.",
  },
];

export default function FAQSection({ variant = "simple" }: FAQSectionProps) {
  if (variant === "tabbed") {
    return (
      <section id="faq-content" className="px-4 md:px-8 lg:px-20 py-10 sm:py-14 md:py-20 lg:py-24 bg-white">
        {/* Header */}
        <div className="flex flex-col items-center text-center mb-8 sm:mb-12">
          <Badge variant="outline" className="font-plus-jakarta text-xs sm:text-sm font-medium text-neutral-500 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 mb-4 sm:mb-6">
            Questions
          </Badge>
          <h2 className="font-plus-jakarta text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900 mb-4 sm:mb-6">
            FREQUENTLY ASKED QUESTIONS
          </h2>
          <p className="font-plus-jakarta text-xs sm:text-sm md:text-base text-neutral-300 max-w-2xl">
            Need more help? Our customer support team is available Monday through Friday, 9 AM to 5 PM CET. Reach us at{" "}
            <a href="mailto:support@wheeltox.com" className="text-neutral-900 underline font-medium">
              support@wheeltox.com
            </a>
          </p>
        </div>

        {/* Tabbed FAQ */}
        <div className="max-w-4xl mx-auto">
          <Tabs defaultValue={categories[0]} className="w-full">
            {/* Tabs container with fade on mobile */}
            <div className="relative mb-8">
              <div className="lg:hidden absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background-50 to-transparent z-10 pointer-events-none rounded-r-xl" />
              <TabsList className="w-full h-auto flex justify-start lg:justify-center overflow-x-auto lg:flex-wrap gap-2 md:gap-4 bg-background-50 rounded-xl [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                {categories.map((category) => (
                  <TabsTrigger
                    key={category}
                    value={category}
                    className="font-plus-jakarta text-sm px-4 py-2 rounded-xl data-[state=active]:bg-white data-[state=active]:text-neutral-900 data-[state=active]:font-medium data-[state=active]:shadow-none text-neutral-300 hover:text-neutral-900 cursor-pointer whitespace-nowrap flex-shrink-0 lg:flex-shrink"
                  >
                    {category}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            {categories.map((category) => (
              <TabsContent key={category} value={category} className="mt-0">
                <div className="bg-background-50 rounded-xl border-none p-4">
                  <Accordion type="single" collapsible className="w-full flex flex-col gap-2">
                    {faqsByCategory[category]?.map((faq, index) => (
                      <AccordionItem
                        key={index}
                        value={`item-${index}`}
                        className="bg-background-100 rounded-xl border border-stroke-light px-6"
                      >
                        <AccordionTrigger className="font-plus-jakarta text-neutral-900 text-left hover:no-underline py-5 cursor-pointer">
                          {faq.question}
                        </AccordionTrigger>
                        <AccordionContent className="font-plus-jakarta text-neutral-300 pb-5">
                          {faq.answer}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>
    );
  }

  // Simple variant (default)
  return (
    <section className="px-4 md:px-8 lg:px-20 pt-0 pb-20">
      {/* Header */}
      <div className="flex flex-col items-center text-center mb-8 sm:mb-12">
          <Badge variant="outline" className="font-plus-jakarta text-xs sm:text-sm font-medium text-neutral-500 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 mb-4 sm:mb-6">
            Questions
          </Badge>
          <h2 className="font-plus-jakarta text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900 mb-4 sm:mb-6">
            FREQUENTLY ASKED QUESTIONS
          </h2>
          <p className="font-plus-jakarta text-xs sm:text-sm md:text-base text-neutral-300 max-w-2xl">
            Need more help? Our customer support team is available Monday through Friday, 9 AM to 5 PM CET. Reach us at{" "}
            <a href="mailto:support@wheeltox.com" className="text-neutral-900 underline font-medium">
              support@wheeltox.com
            </a>
          </p>
        </div>

      {/* FAQ Accordion */}
      <div className="max-w-4xl mx-auto bg-background-50 rounded-xl border-none p-4">
        <Accordion type="single" collapsible className="w-full flex flex-col gap-2">
          {simpleFaqs.map((faq, index) => (
            <AccordionItem
              key={index}
              value={`item-${index}`}
              className="bg-background-100 rounded-xl border border-stroke-light px-6"
            >
              <AccordionTrigger className="font-plus-jakarta text-neutral-900 text-left hover:no-underline py-5">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="font-plus-jakarta text-neutral-300 pb-5">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
